import * as THREE from 'three';
import type { TinLod } from '@/core/landxmlLod';
import { displayToInternalRaw, internalToDisplayRaw } from './alignment';
import type { AlignmentDegradation, DrapeSampler, KeyPoint, StationEquation } from './alignment';
import {
  elementChildren,
  firstChild,
  numAttr,
  readAlignments,
  readBreaklines,
  readParcels,
  readPipeNetworks,
  readSurfaceTins,
  type PipeShape,
  type StructData,
  type SurfaceTin,
} from '@gis/landxml';
import { buildTinSampler, combineSamplers } from './tinSampler';
import type { RawTin } from './landxmlTin';

/** Re-exported so callers have one import for "the LandXML readers refused this file". */
export { LandxmlNonMetreUnitsError } from './landxmlTin';
export type { PipeShape } from '@gis/landxml';

/**
 * Civil categories surfaced to the UI. Used for color-by-category, the
 * properties drawer, and quantity reports.
 */
export type LandXMLCategory =
  | 'SURFACE'
  | 'ALIGNMENT'
  | 'PARCEL'
  | 'BREAKLINE'
  | 'PIPE'
  | 'STRUCTURE';

/**
 * Per-feature metadata. Only the fields relevant to the feature's category
 * are populated — everything else is undefined.
 */
export interface LandXMLFeatureMetadata {
  // Surface
  surfaceArea?: number;       // sum of TIN triangle areas (m²)
  triangleCount?: number;
  vertexCount?: number;
  zMin?: number;
  zMax?: number;
  // Alignment / breakline / pipe centerline
  length?: number;            // 3D polyline length (m)
  alignment?: AlignmentParseResult;
  // Parcel
  parcelArea?: number;        // planar XY area via shoelace (m²)
  // Pipe
  diameter?: number;          // outer diameter (m)
  pipeShape?: PipeShape;
  invertStart?: number;       // invert elevation at start (m)
  invertEnd?: number;         // invert elevation at end (m)
  slope?: number;             // (invertEnd-invertStart)/horizLength
  // Structure
  rimElev?: number;
  sumpElev?: number;
  innerDiameter?: number;
}

export interface LandXMLFeature {
  geometry: THREE.BufferGeometry;
  name: string;
  category: LandXMLCategory;
  metadata: LandXMLFeatureMetadata;
  /**
   * TIN surfaces only: absolute-coordinate vertices kept aside for the drape
   * sampler. `geometry` is translated to layer-local space shortly after
   * parsing, so it cannot be the sampler's source once that has happened —
   * this can, which is what lets `buildSurfaceSampler` defer its grid build.
   */
  drapeSource?: { positions: Float64Array; index: Uint32Array };
  /**
   * TIN surfaces only: a decimated copy drawn in place of `geometry` while the
   * decimation is too fine to see. Built by `buildTinLod` after the origin
   * shift, so it lives in the same layer-local space as `geometry`. Display
   * only — see `core/landxmlLod.ts` for why it must never be measured against.
   */
  lod?: TinLod;
}

export interface PipeNetworkResult {
  /** Pipe + structure meshes (3D solids). */
  meshes: LandXMLFeature[];
  /** Pipe centerlines (lines). */
  lines: LandXMLFeature[];
}

// --- geometry math helpers ----------------------------------------------

function polylineLength(points: ReadonlyArray<THREE.Vector3>): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) len += points[i].distanceTo(points[i - 1]);
  return len;
}

/** Shoelace area of a closed XY polygon (last point may be a duplicate of first). */
function polygonAreaXY(points: ReadonlyArray<THREE.Vector3>): number {
  if (points.length < 3) return 0;
  let s = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) * 0.5;
}

/** Sum of triangle areas in a non-indexed positions buffer (3 verts/tri). */
function trianglesArea(positions: ArrayLike<number>): number {
  let total = 0;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  for (let i = 0; i + 8 < positions.length; i += 9) {
    a.set(positions[i], positions[i + 1], positions[i + 2]);
    b.set(positions[i + 3], positions[i + 4], positions[i + 5]);
    c.set(positions[i + 6], positions[i + 7], positions[i + 8]);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    total += ab.cross(ac).length() * 0.5;
  }
  return total;
}

export interface AlignmentParseResult {
  points: Float64Array;
  staStart: number;
  equations: StationEquation[];
  keyPoints: KeyPoint[];
  declaredLength?: number;
  zSource: 'COORDGEOM' | 'PROFILE' | 'DRAPED' | 'NONE';
  /** Elements drawn as straight chords (see `AlignmentDegradation`), station order. */
  degradations: AlignmentDegradation[];
}

export const parseBreaklines = (doc: Document): LandXMLFeature[] =>
  readBreaklines(doc).map(({ surfaceName, name, points }) => {
    const polyline = points.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    return {
      geometry: new THREE.BufferGeometry().setFromPoints(polyline),
      name: `${surfaceName} - ${name}`,
      category: 'BREAKLINE' as const,
      metadata: { length: polylineLength(polyline) },
    };
  });

/**
 * Turn an already-extracted TIN into the same `LandXMLFeature` the DOM path
 * below produces. The geometry is INDEXED, so `computeVertexNormals` averages
 * across shared faces (smooth shading) instead of emitting one normal per
 * triangle corner.
 */
const featureFromTin = (name: string, tin: RawTin): LandXMLFeature | null => {
  if (tin.triangleCount === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(tin.positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(tin.index, 1));
  geometry.computeVertexNormals();
  flipNormalsUp(geometry);
  return {
    geometry,
    name,
    category: 'SURFACE',
    // Absolute-coordinate snapshot for draping. LandXML.tsx translates the
    // geometry by -origin immediately after parsing; this survives that.
    drapeSource: { positions: tin.positions64, index: tin.index },
    metadata: {
      surfaceArea: tin.surfaceArea,
      triangleCount: tin.triangleCount,
      vertexCount: tin.vertexCount,
      zMin: tin.zMin,
      zMax: tin.zMax,
    },
  };
};

/**
 * Flip any normal pointing below the horizon so a TIN is always lit from
 * above, whichever way its faces happen to be wound.
 */
const flipNormalsUp = (geometry: THREE.BufferGeometry) => {
  const normals = geometry.getAttribute('normal');
  if (!normals) return;
  for (let i = 0; i < normals.count; i++) {
    if (normals.getZ(i) < 0) {
      normals.setX(i, -normals.getX(i));
      normals.setY(i, -normals.getY(i));
      normals.setZ(i, -normals.getZ(i));
    }
  }
  normals.needsUpdate = true;
};

/**
 * A surface the reader built from the DOM (a `<Pnts>`/`<Faces>` pair
 * `extractTins` declined, or none were supplied), drawn the way it always has
 * been: a NON-indexed triangle soup, so `computeVertexNormals` gives one flat
 * normal per face. Stats are over the triangles actually drawn, except
 * `vertexCount`, which counts every distinct `<P id>`.
 */
const featureFromDomTin = ({ name, positions: vertices, index }: SurfaceTin): LandXMLFeature => {
  const positions = new Float64Array(index.length * 3);
  let zMin = Infinity;
  let zMax = -Infinity;
  for (let i = 0; i < index.length; i++) {
    const v = index[i] * 3;
    const z = vertices[v + 2];
    positions[i * 3] = vertices[v];
    positions[i * 3 + 1] = vertices[v + 1];
    positions[i * 3 + 2] = z;
    if (z < zMin) zMin = z;
    if (z > zMax) zMax = z;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  flipNormalsUp(geometry);

  return {
    geometry,
    name,
    category: 'SURFACE',
    metadata: {
      surfaceArea: trianglesArea(positions),
      triangleCount: index.length / 3,
      vertexCount: vertices.length / 3,
      zMin: Number.isFinite(zMin) ? zMin : undefined,
      zMax: Number.isFinite(zMax) ? zMax : undefined,
    },
  };
};

/**
 * `tins` are the meshes `extractTins` already pulled out of the raw text; a
 * `<Pnts>` carrying `TIN_INDEX_ATTR` is served from there instead of being
 * re-read from the DOM. Called without them (tests, small files, any document
 * `extractTins` declined) this behaves exactly as it always has.
 *
 * Which surfaces exist, the metres-only refusal and the DOM fallback all come
 * from the shared `readSurfaceTins` (the MAP_2D Lambda reads the same data);
 * this only wraps that data in THREE objects. `assertMetreUnits` inside it is
 * eager, unlike the other readers' guards: a non-metre file is refused whole,
 * and this is the first reader `LandXML.tsx` calls, so it is where the one
 * toast comes from.
 */
export const parseSurfaces = (doc: Document, tins: RawTin[] = []): LandXMLFeature[] => {
  // A fast-path surface's `positions` IS its RawTin's `positions64`; the
  // RawTin carries the Float32 geometry buffer and the precomputed stats.
  const tinOf = new Map(tins.map((t) => [t.positions64, t]));
  const results: LandXMLFeature[] = [];
  for (const surface of readSurfaceTins(doc, tins)) {
    const tin = surface.fromExtractedTin ? tinOf.get(surface.positions) : undefined;
    const feature = tin ? featureFromTin(surface.name, tin) : featureFromDomTin(surface);
    if (feature) results.push(feature);
  }
  return results;
};

export const parseAlignments = (doc: Document, opts: { drape?: DrapeSampler | null } = {}): LandXMLFeature[] =>
  readAlignments(doc).map((a) => {
    let points = new Float64Array(a.points.length * 3);
    a.points.forEach(([x, y, z], i) => {
      points[i * 3] = x;
      points[i * 3 + 1] = y;
      points[i * 3 + 2] = z;
    });
    let zSource: AlignmentParseResult['zSource'] = isFlatArray(points) ? 'NONE' : 'COORDGEOM';
    // Profile / drape: filled in by applyElevation.
    ({ points, zSource } = applyElevation(a.element, points, zSource, a.staStart, a.equations, opts.drape ?? null));
    const polyline: THREE.Vector3[] = [];
    for (let i = 0; i < points.length; i += 3) polyline.push(new THREE.Vector3(points[i], points[i + 1], points[i + 2]));
    return {
      geometry: new THREE.BufferGeometry().setFromPoints(polyline),
      name: a.name,
      category: 'ALIGNMENT' as const,
      metadata: {
        // Analytic (design) 2D length — sum of each element's true length, not
        // the chord-sum of the rendered tessellation, so it matches <Alignment
        // length="..."> to floating-point precision regardless of tessellation
        // density (see parseCoordGeomDetailed).
        length: a.length,
        alignment: {
          points,
          staStart: a.staStart,
          equations: a.equations,
          keyPoints: a.keyPoints,
          declaredLength: a.declaredLength,
          zSource,
          degradations: a.degradations,
        },
      },
    };
  });

function isFlatArray(points: Float64Array): boolean {
  const z0 = points[2];
  for (let i = 5; i < points.length; i += 3) if (Math.abs(points[i] - z0) > 1e-6) return false;
  return true;
}

interface ProfilePvi {
  station: number; // display station as written in the file
  elev: number;
  curveLength: number; // 0 for a plain PVI
}

/** Parse the first <Profile><ProfAlign> of an alignment into PVIs. */
function parseProfile(alignment: Element): ProfilePvi[] | null {
  const profile = firstChild(alignment, 'Profile');
  const profAlign = profile ? firstChild(profile, 'ProfAlign') : null;
  if (!profAlign) return null;
  const out: ProfilePvi[] = [];
  for (const el of elementChildren(profAlign)) {
    const txt = el.textContent?.trim();
    if (!txt) continue;
    const [sta, elev] = txt.split(/\s+/).map(Number);
    if (!Number.isFinite(sta) || !Number.isFinite(elev)) continue;
    let curveLength = 0;
    switch (el.localName) {
      case 'PVI':
        break;
      case 'ParaCurve':
      case 'CircCurve':
        curveLength = numAttr(el, 'length') ?? 0;
        break;
      case 'UnsymParaCurve':
        curveLength = (numAttr(el, 'lengthIn') ?? 0) + (numAttr(el, 'lengthOut') ?? 0);
        break;
      default:
        continue;
    }
    out.push({ station: sta, elev, curveLength });
  }
  return out.length >= 2 ? out.sort((a, b) => a.station - b.station) : null;
}

/** Elevation of a PVI profile at a display station (symmetric parabolic vertical curves). */
function profileElevation(pvis: ProfilePvi[], station: number): number {
  if (station <= pvis[0].station) return pvis[0].elev;
  const last = pvis[pvis.length - 1];
  if (station >= last.station) return last.elev;
  // find the tangent [i, i+1] containing station
  let i = 0;
  while (i < pvis.length - 2 && pvis[i + 1].station <= station) i++;
  const a = pvis[i];
  const b = pvis[i + 1];
  // Two PVIs at the same station would make every grade below Infinity/NaN and
  // poison the vertex Z. Treat the pair as a step and hold `a`'s elevation.
  const run = b.station - a.station;
  if (!(run > 1e-9)) return a.elev;
  const grade = (b.elev - a.elev) / run;
  // vertical curve at PVI b?
  const half = b.curveLength / 2;
  if (half > 0 && station >= b.station - half && i + 2 < pvis.length) {
    const c = pvis[i + 2];
    const g2 = (c.elev - b.elev) / (c.station - b.station);
    const x = station - (b.station - half);
    const bvc = b.elev - grade * half;
    return bvc + grade * x + ((g2 - grade) * x * x) / (2 * b.curveLength);
  }
  // vertical curve at PVI a?
  const halfA = a.curveLength / 2;
  if (halfA > 0 && station <= a.station + halfA && i > 0) {
    const p = pvis[i - 1];
    const g1 = (a.elev - p.elev) / (a.station - p.station);
    const x = station - (a.station - halfA);
    const bvc = a.elev - g1 * halfA;
    return bvc + g1 * x + ((grade - g1) * x * x) / (2 * a.curveLength);
  }
  return a.elev + grade * (station - a.station);
}

/** Insert extra vertices at the given internal stations so profile break points are honoured. */
function insertVerticesAt(points: Float64Array, stations: number[]): Float64Array {
  const n = points.length / 3;
  const cum: number[] = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1] + Math.hypot(points[i * 3] - points[i * 3 - 3], points[i * 3 + 1] - points[i * 3 - 2]));
  const wanted = [...new Set(stations.filter((s) => s > 1e-6 && s < cum[n - 1] - 1e-6))].sort((a, b) => a - b);
  const out: number[] = [];
  let w = 0;
  for (let i = 0; i < n; i++) {
    out.push(points[i * 3], points[i * 3 + 1], points[i * 3 + 2]);
    if (i === n - 1) break;
    const segLen = cum[i + 1] - cum[i];
    while (w < wanted.length && wanted[w] < cum[i + 1] - 1e-6) {
      if (wanted[w] > cum[i] + 1e-6 && segLen > 1e-9) {
        const t = (wanted[w] - cum[i]) / segLen;
        out.push(
          points[i * 3] + (points[i * 3 + 3] - points[i * 3]) * t,
          points[i * 3 + 1] + (points[i * 3 + 4] - points[i * 3 + 1]) * t,
          points[i * 3 + 2] + (points[i * 3 + 5] - points[i * 3 + 2]) * t,
        );
      }
      w++;
    }
  }
  return new Float64Array(out);
}

function applyElevation(
  alignment: Element,
  points: Float64Array,
  zSource: AlignmentParseResult['zSource'],
  staStart: number,
  equations: StationEquation[],
  drape: DrapeSampler | null,
): { points: Float64Array; zSource: AlignmentParseResult['zSource'] } {
  const pvis = parseProfile(alignment);
  let length = 0;
  for (let i = 3; i < points.length; i += 3) length += Math.hypot(points[i] - points[i - 3], points[i + 1] - points[i - 2]);

  if (pvis) {
    // Add vertices at PVIs, BVCs and EVCs so the vertical curve shape survives.
    const breakDisplay: number[] = [];
    for (const p of pvis) {
      breakDisplay.push(p.station);
      if (p.curveLength > 0) breakDisplay.push(p.station - p.curveLength / 2, p.station + p.curveLength / 2);
    }
    const breakInternal = breakDisplay
      .map((d) => displayToInternalRaw(d, staStart, equations, length))
      .filter((s): s is number => s !== null);
    // Also densify long straight segments so parabolas aren't chords (every 10 m inside curves).
    for (const p of pvis) {
      if (p.curveLength <= 0) continue;
      for (let d = p.station - p.curveLength / 2; d <= p.station + p.curveLength / 2; d += 10) {
        const s = displayToInternalRaw(d, staStart, equations, length);
        if (s !== null) breakInternal.push(s);
      }
    }
    const dense = insertVerticesAt(points, breakInternal);
    // Evaluate Z at each vertex's display station.
    const out = new Float64Array(dense);
    let cum = 0;
    for (let i = 0; i < out.length; i += 3) {
      if (i > 0) cum += Math.hypot(out[i] - out[i - 3], out[i + 1] - out[i - 2]);
      out[i + 2] = profileElevation(pvis, internalToDisplayRaw(cum, staStart, equations));
    }
    return { points: out, zSource: 'PROFILE' };
  }

  if (zSource === 'COORDGEOM') return { points, zSource };

  if (drape) {
    const out = new Float64Array(points);
    let hit = 0;
    for (let i = 0; i < out.length; i += 3) {
      const z = drape(out[i], out[i + 1]);
      if (z !== null) {
        out[i + 2] = z;
        hit++;
      }
    }
    if (hit > 0) return { points: out, zSource: 'DRAPED' };
  }
  return { points, zSource: 'NONE' };
}

/**
 * Sampler over every SURFACE feature returned by parseSurfaces(), answering
 * in whatever frame the geometries are in when it is built. Call it on the
 * ABSOLUTE (pre-shift) positions if you want world-coordinate answers —
 * LandXML.tsx translates surfaces by `-origin` for float32 precision, so it
 * builds the sampler first. The returned sampler owns a snapshot of the
 * positions (see buildTinSampler), so translating the geometries afterwards
 * does not disturb it.
 */
export function buildSurfaceSampler(surfaces: LandXMLFeature[]): DrapeSampler {
  // Snapshot the inputs NOW — the caller translates every geometry by -origin
  // moments later, and a sampler must keep answering in absolute coordinates.
  // Surfaces from the fast path already carry an absolute `drapeSource`, so
  // they cost nothing here; only DOM-parsed ones need copying.
  const sources = surfaces
    .filter((s) => s.category === 'SURFACE')
    .map((s) => {
      if (s.drapeSource) return s.drapeSource;
      const p = s.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
      return p ? { positions: Float64Array.from(p.array as ArrayLike<number>), index: undefined } : null;
    })
    .filter((s): s is { positions: Float64Array; index?: Uint32Array } => !!s);

  // Building the uniform grid is the expensive half — for a 2.4 M-triangle TIN
  // it allocates ~700 k JS arrays. Most LandXML layers are never draped onto
  // (a pure DTM export has no alignments at all), so defer it until the first
  // query actually arrives.
  let grid: DrapeSampler | null = null;
  return (x, y) => {
    if (!grid) grid = combineSamplers(sources.map((s) => buildTinSampler(s.positions, s.index)));
    return grid(x, y);
  };
}

export const parseParcels = (doc: Document): LandXMLFeature[] =>
  readParcels(doc).map(({ name, ring }) => {
    const polyline = ring.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    return {
      geometry: new THREE.BufferGeometry().setFromPoints(polyline),
      name,
      category: 'PARCEL' as const,
      metadata: { parcelArea: polygonAreaXY(polyline), length: polylineLength(polyline) },
    };
  });

// --- Pipe Network types and helpers ---

const buildPipeCylinder = (start: THREE.Vector3, end: THREE.Vector3, radius: number): THREE.BufferGeometry => {
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = direction.length();
  if (length < 0.001) return new THREE.BufferGeometry();

  const geometry = new THREE.CylinderGeometry(radius, radius, length, 8);
  const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion();
  quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());

  geometry.applyQuaternion(quaternion);
  geometry.translate(midpoint.x, midpoint.y, midpoint.z);
  return geometry;
};

const buildStructGeometry = (struct: StructData): THREE.BufferGeometry => {
  const radius = (struct.outerDiameter || struct.innerDiameter || 1.0) / 2;
  const height = struct.height || 2.0;
  const [x, y, z] = struct.center;

  const geometry = new THREE.CylinderGeometry(radius, radius, height, 12);
  geometry.rotateX(Math.PI / 2);
  geometry.translate(x, y, z + height / 2);
  return geometry;
};

export const parsePipeNetworks = (doc: Document): PipeNetworkResult => {
  const meshes: LandXMLFeature[] = [];
  const lines: LandXMLFeature[] = [];
  for (const network of readPipeNetworks(doc)) {
    const structMap = new Map<string, StructData>();
    for (const s of network.structs) {
      structMap.set(s.name, s);
      const structGeo = buildStructGeometry(s);
      if (structGeo.getAttribute('position')?.count) {
        meshes.push({
          geometry: structGeo,
          name: `${network.name} - ${s.name}`,
          category: 'STRUCTURE',
          metadata: { rimElev: s.rimElev, sumpElev: s.sumpElev, innerDiameter: s.innerDiameter },
        });
      }
    }

    for (const pipe of network.pipes) {
      const startStruct = structMap.get(pipe.refStart);
      const endStruct = structMap.get(pipe.refEnd);
      if (!startStruct || !endStruct) continue;
      const pipeRadius = pipe.diameter / 2;

      const invertStart = startStruct.invertElevs.get(pipe.name) ?? startStruct.center[2];
      const invertEnd = endStruct.invertElevs.get(pipe.name) ?? endStruct.center[2];
      const startZ = invertStart + pipeRadius;
      const endZ = invertEnd + pipeRadius;

      const startPt = new THREE.Vector3(startStruct.center[0], startStruct.center[1], startZ);
      const endPt = new THREE.Vector3(endStruct.center[0], endStruct.center[1], endZ);
      const length3D = startPt.distanceTo(endPt);
      const horizLength = Math.hypot(
        endStruct.center[0] - startStruct.center[0],
        endStruct.center[1] - startStruct.center[1],
      );
      const slope = horizLength > 0 ? (invertEnd - invertStart) / horizLength : 0;

      const metadata: LandXMLFeatureMetadata = {
        diameter: pipe.diameter,
        pipeShape: pipe.shape,
        invertStart,
        invertEnd,
        slope,
        length: length3D,
      };

      const pipeGeo = buildPipeCylinder(startPt, endPt, pipeRadius);
      if (pipeGeo.getAttribute('position')?.count) {
        meshes.push({
          geometry: pipeGeo,
          name: `${network.name} - ${pipe.name}`,
          category: 'PIPE',
          metadata,
        });
      }

      const lineGeo = new THREE.BufferGeometry().setFromPoints([startPt, endPt]);
      lines.push({
        geometry: lineGeo,
        name: `${network.name} - ${pipe.name} (centerline)`,
        category: 'PIPE',
        metadata,
      });
    }
  }

  return { meshes, lines };
};
